// React
import { useContext, useEffect } from "react"

// Components
import { ActionButton, Label, Loading, NumberInput } from "components"

// Contexts
import { DetailContext, DropdownContext } from "../contexts"

// Form
import { Controller, useForm, useFormContext } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Types
import type { RowType } from "../types"

// Utils
import { convertNumber, useToggle } from "utils"
import { type FormType, DetailType } from "../utils"

export function ReceivedDetail(): JSX.Element {
  // Hooks
  const { fields } = useContext(DetailContext)

  return (
    <tbody>
      {!fields.length ? (
        <tr>
          <td colSpan={10}>
            <Loading errorText="No data available" />
          </td>
        </tr>
      ) : fields.map((item, key) => {
        return (
          <Row
            key={item.id}
            index={key}
            item={item}
          />
        )
      })}
    </tbody>
  )
}

function Row(params: {
  item: RowType
  index: number
}): JSX.Element {
  // Vars
  const index = params.index
  const item = params.item

  // Hooks
  const { isActive, toggle } = useToggle(false)

  if (isActive) {
    return (
      <FormSection
        index={index}
        item={item}
        toggle={toggle}
      />
    )
  }

  return (
    <ViewSection
      index={index}
      item={item}
      toggle={toggle}
    />
  )
}

function FormSection(params: {
  index: number
  item: RowType
  toggle: () => void
}): JSX.Element {
  // Hooks
  const dropdown = useContext(DropdownContext)
  const { update } = useContext(DetailContext)

  // Vars
  const item = params.item
  const toggle = params.toggle
  const product = dropdown.product.find(i => i.product_id === item.product_id)
  const taxData = dropdown.tax.find(i => i.value === item.tax_id.toString())?.label

  // Form
  const { control, handleSubmit, watch, setValue } = useForm<DetailType>({
    defaultValues: params.item
  })

  const qtyReceived = watch("qty_received_item")

  useEffect(() => {
    const total = (qtyReceived ?? 0) * item.price_received 
    const discounted = total - (total * (item.discount ?? 0)/100)
    const tax_paid = (total * (item.tax_rate ?? 0)/100)
    setValue("total",discounted)
    setValue("total_tax_paid",tax_paid);
    
  }, [qtyReceived,item.price_received, item.discount, item.tax_rate, setValue])

  const { clearErrors } = useFormContext<FormType>()

  const onSubmit = (value: DetailType) => {
    clearErrors()
    update(params.index, value)
    toggle()
  }

  return (
    <tr>
      <td className="text-right">{item.qty_back_delivered}</td>

      <td className="text-right">
        <Controller
          name="qty_received_item"
          control={control}
          render={({ field, fieldState }) => (
            <NumberInput
              inputClass="input-sm"
              error={fieldState.error?.message}
              value={field.value}
              ref={field.ref}
              onValueChange={res => {
                const qty = res.floatValue || 0
                field.onChange(qty)
              }}
            />
          )}
        />
      </td>

      <td className="text-right">{item.unit_measure}</td>
      <td className="text-center">{product?.product_barcode}</td>
      <td>{product?.product_name}</td>
      <td className="text-right">{convertNumber(item.price_received).intoCurrency}</td>
      <td className="text-right">{convertNumber(item.discount).intoCurrency}%</td>
      <td className="text-right">{convertNumber(watch("total")).intoCurrency}</td>
      <td>{taxData}</td>

      <td>
        <section className="flex justify-center">
          <ActionButton.Check onClick={handleSubmit(onSubmit)} />
          <ActionButton.Close onClick={toggle} />
        </section>
      </td>
    </tr>
  )
}

function ViewSection(params: {
  item: RowType
  index: number
  toggle: () => void
}): JSX.Element {
  // Hooks
  const dropdown = useContext(DropdownContext)

  // Vars
  const item = params.item
  const product = dropdown.product.find(i => i.product_id === item.product_id)
  const taxData = dropdown.tax.find(i => i.value === item.tax_id.toString())?.label

  // Form
  const { formState: { disabled, errors } } = useFormContext<FormType>()

  return (
    <tr>
      <td className="text-right">{item.qty_back_delivered}</td>

      <td>
        <div className="text-right">{item.qty_received_item}</div>
        <ErrorMessage
          errors={errors}
          name={`received_reject_detail.${params.index}.qty_received_item`}
          render={({ message }) => (
            <Label
              error
              text={message}
            />
          )}
        />
      </td>

      <td className="text-right">{item.unit_measure}</td>
      <td className="text-center">{product?.product_barcode}</td>
      <td>{product?.product_name}</td>
      <td className="text-right">{convertNumber(item.price_received).intoCurrency}</td>
      <td className="text-right">{convertNumber(item.discount).intoCurrency}%</td>
      <td className="text-right">{convertNumber(item.total).intoCurrency}</td>
      <td>{taxData}</td>

      <td>
        {!disabled && (
          <section className="flex justify-center">
            <ActionButton.Update onClick={params.toggle} />
          </section>
        )}
      </td>
    </tr>
  )
}